import React, {
  useState,
  useRef,
  FunctionComponent,
  useCallback,
  useEffect,
} from "react";
import { VerifyMail, Eye, LockGrey, MailGrey } from "@/components/bnp-icons";
import { Button } from "@/components/bnp-ui";
import { TextInput } from "@mantine/core";
import AuthWrapper from "@/components/layouts/AuthWrapper";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { AuthService } from "@/services";
import parseHttpError from "@/utils/parseHttpError";
import { useMutation } from "@tanstack/react-query";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { toast } from "react-toastify";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";

const Verify = () => {
  const router = useRouter();
  const ph = usePostHogAnalytics();
  const { token, a } = router.query;
  const email = token ? atob(token as string) : "";

  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);

  const defaultValues = {
    email: email,
    otp_code: otp.join(""),
  };

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email("Enter a valid email")
      .required("Enter your email"),
    otp_code: yup.string().required("Enter a valid verification code"),
  });

  const {
    getValues,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const inputRefs = useRef<HTMLInputElement[]>([]);
  const [cooldown, setCooldown] = useState(0);

  // Add cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);
  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1) {
      const updatedOtp = [...otp];
      updatedOtp[index] = value;
      setOtp(updatedOtp);

      if (value.length === 1 && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace" && otp[index] === "" && index > 0) {
      handleOtpChange(index - 1, "");
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (
    index: number,
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text");
    const digits = pasted.replace(/[^0-9]/g, "").slice(0, 6 - index);
    if (!digits) return;

    const updatedOtp = [...otp];
    for (let i = 0; i < digits.length; i++) {
      updatedOtp[index + i] = digits[i];
    }
    setOtp(updatedOtp);

    const target = index + digits.length;
    if (target < 6) {
      inputRefs.current[target]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const setInputRef = useCallback(
    (ref: HTMLInputElement | null, index: number) => {
      if (ref) {
        inputRefs.current[index] = ref;
      }
    },
    [],
  );

  const verifyRequest = async () => {
    try {
      console.log(otp.join(""), email);
      const response = await AuthService.verifyUser({
        otp_code: otp.join(""),
        email,
      });
      return response.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const resendOTPRequest = async () => {
    try {
      const response = await AuthService.resendOTP({
        email,
      });
      return response.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const verifyResetOTPRequest = async () => {
    try {
      console.log(otp.join(""), email);
      const response = await AuthService.verifyResetOTP({
        otp_code: otp.join(""),
        email,
      });
      return response.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const mutation: any = useMutation({
    mutationFn: verifyRequest,
    onSuccess: (res: any) => {
      toast.success("Your account has been verified!");
      const { email, full_name, role } = res.data;
      ph.trackEvent("email_verified", { role: role || undefined });

      const encodedEmail = btoa(email);
      const encodedFullName = btoa(full_name);

      setTimeout(() => {
        router.push(
          `/auth/onboarding?token=${encodedEmail}&id=${encodedFullName}&role=${role || ""}`,
        );
      }, 2000);
    },

    onError: (error: any) => {
      toast.error(error.message || "An error occurred");
    },
  });
  const resendMutation = useMutation({
    mutationFn: resendOTPRequest,
    onSuccess: (res: any) => {
      toast.success("New verification code sent!");
      setOtp(["", "", "", "", "", ""]);
      setCooldown(60);
      inputRefs.current[0]?.focus();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to resend verification code");
    },
  });

  const verifyResetOTPMutation = useMutation({
    mutationFn: verifyResetOTPRequest,
    onSuccess: (res: any) => {
      toast.success("Verification success!");
      const encodedEmail = btoa(email);
      setTimeout(() => {
        router.push(`/auth/reset-password?token=${encodedEmail}&a=reset`);
      }, 2000);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to verify code");
    },
  });

  return (
    <AuthWrapper>
      <div className="flex flex-col w-[100%] lg:w-[452px]">
        <div className="flex flex-col items-center gap-4">
          <VerifyMail />
          <div className="flex flex-col gap-1 items-center">
            <h2 className="text-main-900 font-semibold text-xl">
              Enter Verification Code
            </h2>
            <p className="text-grey-7 text-sm font-normal text-center">
              We&apos;ve sent a code to{" "}
              <span className="text-bnp-cyan">{email}</span>
            </p>
          </div>
        </div>

        <form
          action="submit"
          className=" w-[100%] mt-6"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="flex justify-center gap-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => handleOtpPaste(index, e)}
                ref={(ref) => setInputRef(ref, index)}
                className="w-[45px] lg:w-[67px] h-[64px] border border-grey-7 rounded-[10px] text-center text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-bnp-cyan focus:border-bnp-cyan"
              />
            ))}
          </div>
          <Button
            className="bg-bnp-dark text-white text-sm font-extrabold w-[100%] h-[52px] rounded-lg mt-8 "
            isLoading={mutation.isPending || verifyResetOTPMutation.isPending}
            onClick={() => {
              a === "reset"
                ? verifyResetOTPMutation.mutate()
                : mutation.mutate();
            }}
          >
            Verify OTP
          </Button>
        </form>

        {a !== "reset" && (
          <div className="flex flex-col gap-2 items-center self-center text-sm text-grey-7 mt-6 ">
            <p>Experiencing issues receiving the code?</p>
            {cooldown > 0 ? (
              <p className="text-sm text-main-900">
                Resend available in {cooldown} seconds
              </p>
            ) : (
              <button
                onClick={() => resendMutation.mutate()}
                disabled={resendMutation.isPending || cooldown > 0}
                className={`text-sm font-medium text-main-900 underline text-center 
          ${
            resendMutation.isPending || cooldown > 0
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer hover:text-main-800"
          }`}
              >
                {resendMutation.isPending ? "Resending..." : "Resend code"}
              </button>
            )}
          </div>
        )}
      </div>
    </AuthWrapper>
  );
};

export default Verify;
